<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "TOCTemp1",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: []
        };
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.splitContentIntoPages();
        });
    },
    methods: {
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages];

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let currentPageItems = allTocItems.splice(0, itemsPerPage);

                this.pages.push({
                    items: currentPageItems,
                    projects: [
                        projects[pagesProjectsIndex] || null,
                        projects[pagesProjectsIndex + 1] || null,
                    ],
                });
                pagesProjectsIndex += 2;
            }

            if (this.pages.length === 0) {
                this.pages.push({ items: [], projects: [null, null] });
            }
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': (toc.style.colorScheme && toc.style.colorScheme.header) || (toc.style.box && toc.style.box.color) || '#545454',
        '--body-color': (toc.style.colorScheme && toc.style.colorScheme.body) || (toc.style.box && toc.style.box.color) || '#ffffff',
        '--footer-color': (toc.style.colorScheme && toc.style.colorScheme.footer) || (toc.style.box && toc.style.box.color) || '#545454',
        '--heading-color': toc.style.heading.color,
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color,
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#000000',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">
            <div class="banner top-banner">
                <h2>
                    <SpellCheckedText :value="toc.title || ''" @input="toc.title = $event" />
                </h2>
            </div>

            <div class="page-content">
                <div class="split-layout">
                    <div class="toc-list-sec">
                        <div v-for="(item, idx) in page.items" :key="idx" class="toc-item">
                            <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                            <span class="heading">
                                <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                            </span>
                        </div>
                    </div>

                    <div class="img-column">
                        <div v-for="(proj, idx) in page.projects" :key="idx" class="img-wrapper photobox">
                            <PhotoBox v-if="proj" :project="proj" height="300px"
                                :bgColor="toc.style.colorScheme && toc.style.colorScheme.body" />
                            <div v-else class="imageBox" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="bottom-banner"></div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        display: flex;
        flex-direction: column;
        height: 296.83mm;
        overflow: hidden;

        .top-banner {
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--header-color);
            min-height: 60px;

            h2 {
                font-size: 18px;
                color: white;
                margin: 0;
                font-weight: 700;
                font-family: Helvetica;
            }
        }

        .page-content {
            flex: 1;
            min-height: 0;
            display: flex;
            background: white;
            padding: 30px;

            .split-layout {
                flex: 1;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
                min-height: 0;
                width: 100%;
                margin: auto;

                .toc-list-sec {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 15px;
                    min-height: 0;
                    overflow: hidden;

                    .toc-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 10px;
                        width: 100%;

                        .page-num {
                            min-width: 25px;
                            flex-shrink: 0;
                            white-space: nowrap;
                            color: var(--label-color);
                            font-size: var(--label-size);
                            font-weight: var(--label-weight);
                            font-family: var(--label-font);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                        }

                        .heading {
                            flex: 1;
                            color: var(--text-color);
                            font-size: var(--text-size);
                            font-weight: var(--text-weight);
                            font-family: var(--text-font);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            min-width: 0;
                        }
                    }
                }

                .img-column {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 50px;

                    .img-wrapper {
                        display: flex;
                        flex-direction: column;

                        .imageBox {
                            width: 100%;
                            height: 300px;
                            background: var(--body-color);
                        }
                    }
                }
            }
        }

        .bottom-banner {
            background: var(--footer-color);
            min-height: 60px;
        }
    }
}
</style>
